import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import {
  FileSpreadsheet, Plus, Download, X, Loader2, Check, FilePlus2, Eye,
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 25;

const STATUS_STYLES = {
  draft: "bg-[#EEEBF5] text-[#5B3AA4]",
  sent:  "bg-[#DDECFF] text-[#1D4ED8]",
  paid:  "bg-[#DCF5D9] text-[#4A7C1E]",
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const r = await api.get("/invoices", { params: { page: p, limit: PAGE_SIZE } });
      setRows(r.data.data); setPage(r.data.page);
      setPages(r.data.pages); setTotal(r.data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  const patchStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { status });
      toast.success(`${inv.code} marked ${status}`);
      load(page);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCsv = async (inv) => {
    try {
      const resp = await fetch(`${API_BASE}/invoices/${inv.id}/export`, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${inv.code}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="accp-label">Billing</div>
          <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            Invoices
          </h1>
          <p className="text-muted-foreground mt-1">
            Batch approved timesheets into invoices. Double-invoicing is prevented by a hard lock.
          </p>
        </div>
        <button onClick={() => setNewOpen(true)} className="accp-btn-primary" data-testid="new-invoice-btn">
          <Plus size={14} /> New invoice
        </button>
      </div>

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Invoice</th>
                <th className="p-4 accp-label">Period</th>
                <th className="p-4 accp-label">Funding</th>
                <th className="p-4 accp-label">Hours</th>
                <th className="p-4 accp-label">Amount</th>
                <th className="p-4 accp-label">Status</th>
                <th className="p-4 accp-label text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">
                  <FileSpreadsheet size={22} className="inline mr-2 text-primary" />
                  No invoices yet. Click <b>New invoice</b> to batch approved timesheets.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-accent/20">
                  <td className="p-4 font-semibold text-primary">{r.code}</td>
                  <td className="p-4 text-muted-foreground">{r.period_start} → {r.period_end}</td>
                  <td className="p-4 text-muted-foreground">{r.funding_source || "—"}</td>
                  <td className="p-4">{r.total_hours}</td>
                  <td className="p-4">${Number(r.total_amount || 0).toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      <button onClick={() => setDetailId(r.id)} className="accp-btn-ghost text-xs"
                              data-testid={`view-${r.code}`}>
                        <Eye size={12} /> View
                      </button>
                      <button onClick={() => downloadCsv(r)} className="accp-btn-ghost text-xs"
                              data-testid={`export-${r.code}`}>
                        <Download size={12} /> CSV
                      </button>
                      {r.status === "draft" && (
                        <button onClick={() => patchStatus(r, "sent")} className="accp-btn-ghost text-xs"
                                data-testid={`mark-sent-${r.code}`}>
                          Mark sent
                        </button>
                      )}
                      {r.status !== "paid" && (
                        <button onClick={() => patchStatus(r, "paid")} className="accp-btn-ghost text-xs"
                                data-testid={`mark-paid-${r.code}`}>
                          <Check size={12} /> Mark paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onChange={load} testId="invoices-pagination" />
      </div>

      {newOpen && <NewInvoice onClose={() => setNewOpen(false)} onCreated={(inv) => {
        setNewOpen(false); load(1); setDetailId(inv.id);
      }} />}
      {detailId && <InvoiceDetail id={detailId} onClose={() => setDetailId(null)}
                                   onRefresh={() => load(page)} />}
    </div>
  );
}

/* ---------------- New Invoice modal ---------------- */
function NewInvoice({ onClose, onCreated }) {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(iso(first));
  const [end, setEnd] = useState(iso(last));
  const [funding, setFunding] = useState("");
  const [fundingOptions, setFundingOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get("/funding-sources").then((r) => setFundingOptions(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setSel(new Set());
    try {
      const r = await api.get("/invoices/available-timesheets", {
        params: { period_start: start, period_end: end, funding_source: funding || undefined },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, funding]);

  const toggle = (id) => {
    setSel((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (sel.size === rows.length) setSel(new Set());
    else setSel(new Set(rows.map((r) => r.id)));
  };

  const totals = useMemo(() => {
    const selected = rows.filter((r) => sel.has(r.id));
    const hrs = selected.reduce((s, r) => s + (Number(r.hours) || 0), 0);
    const amt = selected.reduce((s, r) => s + (Number(r.hours) || 0) * (Number(r.billing_rate_snapshot) || 0), 0);
    return { count: selected.length, hours: hrs.toFixed(2), amount: amt.toFixed(2) };
  }, [sel, rows]);

  const create = async () => {
    if (sel.size === 0) return;
    if (!window.confirm(`Create invoice for ${sel.size} timesheet(s) totalling ${totals.hours} hrs?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/invoices", {
        period_start: start, period_end: end,
        funding_source: funding || null,
        timesheet_ids: Array.from(sel),
        notes: notes || null,
      });
      toast.success(`Invoice ${r.data.code} created — ${sel.size} timesheets, ${totals.hours} hrs`);
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card accp-modal-card-lg space-y-4"
           data-testid="new-invoice-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">Billing</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              New invoice
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="accp-label">Period start</span>
            <input type="date" className="accp-input mt-1" value={start}
                   onChange={(e) => setStart(e.target.value)} data-testid="inv-start" />
          </label>
          <label className="block">
            <span className="accp-label">Period end</span>
            <input type="date" className="accp-input mt-1" value={end}
                   onChange={(e) => setEnd(e.target.value)} data-testid="inv-end" />
          </label>
          <label className="block">
            <span className="accp-label">Funding source</span>
            <select className="accp-input mt-1" value={funding}
                    onChange={(e) => setFunding(e.target.value)} data-testid="inv-funding">
              <option value="">All</option>
              {fundingOptions.map((f) => (
                <option key={f.id} value={f.code}>{f.code} — {f.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="accp-card overflow-hidden">
          <div className="accp-table-scroll max-h-[40vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-accent/30 text-left sticky top-0">
                <tr>
                  <th className="p-2">
                    <input type="checkbox" onChange={toggleAll}
                           checked={rows.length > 0 && sel.size === rows.length}
                           data-testid="inv-select-all" />
                  </th>
                  <th className="p-2 accp-label">Timesheet</th>
                  <th className="p-2 accp-label">Staff</th>
                  <th className="p-2 accp-label">Date</th>
                  <th className="p-2 accp-label">Hours</th>
                  <th className="p-2 accp-label">Rate</th>
                  <th className="p-2 accp-label">Funding</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                    <Loader2 size={12} className="animate-spin inline mr-1" /> Loading…
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground" data-testid="inv-empty">
                    No approved, uninvoiced timesheets in this period.
                  </td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className={`border-t border-border ${sel.has(r.id) ? "bg-accent/30" : ""}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)}
                             data-testid={`inv-sel-${r.code}`} />
                    </td>
                    <td className="p-2 font-semibold text-primary">{r.code}</td>
                    <td className="p-2">{r.staff_name}</td>
                    <td className="p-2 text-muted-foreground">{(r.clock_in || "").slice(0, 10)}</td>
                    <td className="p-2">{Number(r.hours || 0).toFixed(2)}</td>
                    <td className="p-2">${Number(r.billing_rate_snapshot || 0).toFixed(2)}</td>
                    <td className="p-2 text-muted-foreground">{r.funding_source_code_snapshot || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <label className="block">
          <span className="accp-label">Notes (optional)</span>
          <textarea className="accp-input mt-1" rows={2} value={notes}
                    onChange={(e) => setNotes(e.target.value)} data-testid="inv-notes" />
        </label>

        <div className="flex items-center justify-between gap-3 rounded-xl bg-accent/40 p-3 text-sm">
          <div data-testid="inv-totals">
            <span className="font-semibold">{totals.count}</span> selected ·{" "}
            <span className="font-semibold">{totals.hours}h</span> ·{" "}
            <span className="font-semibold">${totals.amount}</span>
          </div>
          <button onClick={create} disabled={busy || sel.size === 0}
                  className="accp-btn-primary" data-testid="inv-create-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} />}
            Create invoice
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Invoice detail ---------------- */
function InvoiceDetail({ id, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const load = () => api.get(`/invoices/${id}`).then((r) => {
    setData(r.data); setNotes(r.data.invoice.notes || "");
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (s) => {
    try {
      await api.patch(`/invoices/${id}`, { status: s });
      toast.success(`Marked ${s}`); load(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveNotes = async () => {
    if ((data.invoice.notes || "") === notes) return;
    setSavingNotes(true);
    try { await api.patch(`/invoices/${id}`, { notes }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingNotes(false); }
  };
  const downloadCsv = async () => {
    const resp = await fetch(`${API_BASE}/invoices/${id}/export`, { credentials: "include" });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data.invoice.code}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!data) {
    return (
      <div className="accp-modal-shell" onClick={onClose}>
        <div className="accp-modal-card text-center text-muted-foreground" data-testid="invoice-detail-loading">
          <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
        </div>
      </div>
    );
  }

  const inv = data.invoice;
  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="accp-modal-card accp-modal-card-lg space-y-4"
           data-testid="invoice-detail-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">Invoice {inv.code}</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              {inv.period_start} → {inv.period_end}
            </h3>
            <div className="mt-1 text-xs text-muted-foreground">
              Funding source: {inv.funding_source || "—"} · Created {new Date(inv.created_at).toLocaleDateString()}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div className="accp-card p-4">
            <div className="accp-label">Total hours</div>
            <div className="text-2xl font-semibold text-primary mt-1">{inv.total_hours}</div>
          </div>
          <div className="accp-card p-4">
            <div className="accp-label">Total amount</div>
            <div className="text-2xl font-semibold text-primary mt-1">${Number(inv.total_amount || 0).toFixed(2)}</div>
          </div>
          <div className="accp-card p-4">
            <div className="accp-label">Status</div>
            <div className={`inline-block text-sm px-2 py-1 rounded-full font-semibold mt-1 ${STATUS_STYLES[inv.status] || ""}`}>
              {inv.status}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={downloadCsv} className="accp-btn-outline text-xs" data-testid="detail-export">
            <Download size={12} /> Export CSV
          </button>
          {inv.status === "draft" && (
            <button onClick={() => setStatus("sent")} className="accp-btn-outline text-xs" data-testid="detail-mark-sent">
              Mark sent
            </button>
          )}
          {inv.status !== "paid" && (
            <button onClick={() => setStatus("paid")} className="accp-btn-primary text-xs" data-testid="detail-mark-paid">
              <Check size={12} /> Mark paid
            </button>
          )}
        </div>

        <div className="accp-card overflow-hidden">
          <div className="accp-table-scroll max-h-[40vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-accent/30 text-left sticky top-0">
                <tr>
                  <th className="p-2 accp-label">Timesheet</th>
                  <th className="p-2 accp-label">Staff</th>
                  <th className="p-2 accp-label">Date</th>
                  <th className="p-2 accp-label">Hours</th>
                  <th className="p-2 accp-label">Rate</th>
                  <th className="p-2 accp-label">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.timesheets.map((t) => {
                  const h = Number(t.hours || 0);
                  const rate = Number(t.billing_rate_snapshot || 0);
                  return (
                    <tr key={t.id} className="border-t border-border">
                      <td className="p-2 font-semibold text-primary">{t.code}</td>
                      <td className="p-2">{t.staff_name}</td>
                      <td className="p-2 text-muted-foreground">{(t.clock_in || "").slice(0, 10)}</td>
                      <td className="p-2">{h.toFixed(2)}</td>
                      <td className="p-2">${rate.toFixed(2)}</td>
                      <td className="p-2">${(h * rate).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-primary">
                  <td colSpan={3} className="p-2 font-semibold text-primary">Total</td>
                  <td className="p-2 font-semibold text-primary">{inv.total_hours}</td>
                  <td></td>
                  <td className="p-2 font-semibold text-primary">${Number(inv.total_amount || 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <label className="block">
          <span className="accp-label">Notes</span>
          <textarea className="accp-input mt-1" rows={2} value={notes}
                    onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes}
                    disabled={savingNotes} data-testid="detail-notes" />
        </label>
      </div>
    </div>
  );
}
